import CONST from "@/config/const";
import SEO_CONFIG from "@/config/seo.config.json";
import {
  DEFAULT_LANG,
  KNOWN_PATH_LANGS,
  buildLangPath,
  getLangFromPath,
  stripLangPrefix,
} from "@/utils/langPath";
import { buildStructuredData } from "@/utils/buildStructuredData";

export const SEO_ORIGIN = SEO_CONFIG.origin;
export const DEFAULT_SOCIAL_IMAGE = SEO_CONFIG.defaultSocialImage;

const INDEXABLE_PATHS = SEO_CONFIG.indexablePages;
const STRUCTURED_DATA_SELECTOR =
  'script[type="application/ld+json"][data-seo="structured"]';

function removeAll(selector) {
  document.querySelectorAll(selector).forEach((node) => node.remove());
}

function upsertMeta(attribute, key, content) {
  let node = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attribute, key);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

function appendLink(rel, href, hreflang) {
  const node = document.createElement("link");
  node.setAttribute("rel", rel);
  node.setAttribute("href", href);
  if (hreflang) node.setAttribute("hreflang", hreflang);
  document.head.appendChild(node);
}

export function getHtmlLang(lang) {
  return (
    SEO_CONFIG.languages[lang]?.htmlLang || lang.replace("_", "-").toLowerCase()
  );
}

export function getIndexablePage(pathname = window.location.pathname) {
  const pagePath = stripLangPrefix(pathname).replace(/\/+$/, "") || "/";
  return INDEXABLE_PATHS.includes(pagePath) ? pagePath : null;
}

export function getSiteOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return SEO_ORIGIN;
}

export function buildCanonicalUrl(lang, pagePath) {
  return `${getSiteOrigin()}${buildLangPath(lang, pagePath === "/" ? "" : pagePath)}`;
}

export function getSeoInfo(lang, pagePath) {
  return (
    CONST.seo[lang]?.[pagePath] ||
    CONST.seo[lang]?.["/"] ||
    CONST.seo[DEFAULT_LANG]["/"]
  );
}

function upsertStructuredData(data) {
  removeAll(STRUCTURED_DATA_SELECTOR);
  const node = document.createElement("script");
  node.type = "application/ld+json";
  node.setAttribute("data-seo", "structured");
  node.textContent = JSON.stringify(data);
  document.head.appendChild(node);
}

export function removeIndexableMeta() {
  removeAll('link[rel="canonical"], link[rel="alternate"][hreflang]');
  removeAll('meta[property^="og:"], meta[name^="twitter:"]');
  removeAll(STRUCTURED_DATA_SELECTOR);
}

export function applyNoIndexMeta(lang = getLangFromPath()) {
  document.documentElement.setAttribute("lang", getHtmlLang(lang));
  removeIndexableMeta();
  upsertMeta("name", "robots", "noindex,nofollow");
}

export function applySeoMeta({
  lang = getLangFromPath(),
  pathname = window.location.pathname,
  image = DEFAULT_SOCIAL_IMAGE,
} = {}) {
  const pagePath = getIndexablePage(pathname);
  if (!pagePath) {
    applyNoIndexMeta(lang);
    return null;
  }

  const seoInfo = getSeoInfo(lang, pagePath);
  const canonical = buildCanonicalUrl(lang, pagePath);
  const htmlLang = getHtmlLang(lang);
  const siteOrigin = getSiteOrigin();

  document.documentElement.setAttribute("lang", htmlLang);
  document.title = seoInfo.title;
  removeIndexableMeta();
  removeAll('meta[name="robots"]');

  upsertMeta("name", "description", seoInfo.description);
  upsertMeta("name", "keywords", seoInfo.keywords);
  appendLink("canonical", canonical);

  KNOWN_PATH_LANGS.forEach((alternateLang) => {
    appendLink(
      "alternate",
      buildCanonicalUrl(alternateLang, pagePath),
      SEO_CONFIG.languages[alternateLang]?.hreflang ||
        getHtmlLang(alternateLang),
    );
  });
  appendLink(
    "alternate",
    buildCanonicalUrl(DEFAULT_LANG, pagePath),
    "x-default",
  );

  upsertMeta("property", "og:title", seoInfo.title);
  upsertMeta("property", "og:description", seoInfo.description);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:image", image);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", "X.ME");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:site", "@X.ME_Global");
  upsertMeta("name", "twitter:title", seoInfo.title);
  upsertMeta("name", "twitter:description", seoInfo.description);
  upsertMeta("name", "twitter:image", image);
  upsertStructuredData(
    buildStructuredData({
      lang,
      canonical,
      seoInfo,
      siteOrigin,
      inLanguage: htmlLang,
    }),
  );

  return canonical;
}
